import type { WordListData, WordListEntry } from '@/teoCore/TeoApi';
import { arraysEqual } from '@/teoCore/utility/miscFunctions';
import { TeoEventTarget, type TeoEventMap } from '@/teoCore/utility/TeoEventTarget';

import type { TeoText } from '../TeoText';
import type { TeoView } from '../TextUi';
import type { UiWordListEntry, WordListHandler } from './WordListHandler';

export type WordListSubHandlerEvents = {
  currentEntriesChanged: (detail: UiWordListEntry[]) => void;
  entryFocused: (detail: UiWordListEntry | undefined) => void;
};

export abstract class WordListSubHandler {
  lists: WordListData[] = [];
  private currentListIds: number[] = [];
  private _currentEntries: UiWordListEntry[] = [];
  private _focusedEntry: UiWordListEntry | undefined;
  constructor(
    protected wordLists: WordListHandler,
    protected text: TeoText,
    public events: TeoEventTarget<WordListSubHandlerEvents & TeoEventMap>,
    protected viewName: TeoView,
    protected uiWLName: keyof typeof text.ui.state,
    protected uiCategoryName: keyof typeof text.ui.state,
  ) {
    this.wordLists.events.addEventListener('entryMarkerClicked', (relation) => {
      const entry = this.getEntryByRelation(relation);
      if (entry) this.focusEntry(entry);
    });
    this.text.ui.events.addEventListener('view', (viewName: TeoView) => {
      if (viewName === this.viewName) {
        this.selectLists([]);
        this.wordLists.mark(this.currentLists);
      }
    });
    this.text.events.addEventListener('editable', (editable: boolean) => {
      if (!editable) this.focusEntry(undefined);
    });
    this.text.ui.events.addEventListener(this.uiWLName, () => {
      this.syncSelectedLists();
    });
    this.text.ui.events.addEventListener(this.uiCategoryName, () => {
      console.log('Category changed:', this.text.ui.state[this.uiCategoryName]);
      this.syncSelectedLists();
    });
  }

  get currentEntries(): UiWordListEntry[] {
    return this._currentEntries;
  }

  set currentEntries(entries: UiWordListEntry[]) {
    this._currentEntries = entries;
    this.events.triggerEvent('currentEntriesChanged', entries);
  }

  get currentLists(): WordListData[] {
    if (!this.currentListIds.length) return [...this.lists];
    return this.lists.filter((list) => this.currentListIds.includes(list.id));
  }

  get focusedEntry(): UiWordListEntry | undefined {
    return this._focusedEntry;
  }

  getEntryByRelation(relation: string): UiWordListEntry | undefined {
    return this._currentEntries.find((entry) => entry.key === relation);
  }

  focusEntry(entry: UiWordListEntry | undefined): void {
    if (this._focusedEntry === entry) return;
    this._focusedEntry = entry;
    this.text.markers.focusMarkers(entry ? [Number(entry.key)] : []);
    this.events.triggerEvent('entryFocused', entry);
  }

  useSuggestion(entryKey: string, suggestionKey: string): void {
    const entry = this.getEntryByRelation(entryKey);
    if (!entry) {
      console.error('No entry found for key:', entryKey);
      return;
    }
    const suggestion = entry.suggestions.find((s) => s.key === suggestionKey);
    if (!suggestion) {
      console.error('No suggestion found for key:', suggestionKey);
      return;
    }
    this.wordLists.replaceInText(Number(entry.key), suggestion);
  }

  selectLists(ids: number[]): void {
    if (arraysEqual(ids, this.currentListIds)) return;
    this.currentListIds = ids;
    this.syncCurrentEntries();
  }

  getFoundEntries(wordList: WordListData): WordListEntry[] {
    return this.wordLists.getWordListFoundEntries(wordList);
  }

  protected syncSelectedLists(): void {
    const selectedWordList = this.text.ui.state[this.uiWLName];
    const selectedCategory = this.text.ui.state[this.uiCategoryName];
    if (selectedWordList) {
      const list = this.lists.find((l) => l.name === selectedWordList);
      if (list) {
        this.selectLists([list.id]);
        this.focusEntry(undefined);
      } else {
        console.warn('Word list not found:', selectedWordList);
        this.selectLists([]);
      }
    } else if (selectedCategory) {
      let lists = [];
      if (selectedCategory === 'checks') {
        lists = this.lists.filter((l) => l.relation_type === 5);
      } else {
        lists = this.lists.filter((l) => l.relation_type !== 5);
      }
      if (lists.length > 0) {
        this.selectLists(lists.map((l) => l.id));
        this.focusEntry(undefined);
      } else {
        console.warn('No word lists found for category:', selectedCategory);
        this.selectLists([]);
      }
    } else {
      this.selectLists([]);
      this.focusEntry(undefined);
    }
  }

  protected createUiWordListEntry(entry: WordListEntry, wordList: WordListData): UiWordListEntry {
    return this.wordLists.createUiWordListEntry(entry, wordList);
  }

  protected syncCurrentEntries() {
    if (!this.lists) {
      console.error('No word lists available.');
      this.currentEntries = [];
      return;
    }
    const entries: UiWordListEntry[] = [];
    this.lists.forEach((wordList) => {
      if (this.currentListIds.includes(wordList.id) || !this.currentListIds.length) {
        entries.push(
          ...this.getFoundEntries(wordList).map((entry) => {
            return this.createUiWordListEntry(entry, wordList);
          }),
        );
      }
    });
    this.currentEntries = entries;
    if (this.text.ui.state.view === this.viewName) {
      this.wordLists.mark(this.currentLists);
    }
  }
}
